import useQueryParams from 'Frontend/hooks/useQueryParams'
import { BlogItem } from 'Frontend/scripts/models/communityBlog.model'
import { quillModules } from 'Frontend/scripts/quillModules'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { Button, Form, Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ReactQuill from 'react-quill-new'
import * as blogService from 'Frontend/scripts/services/communityBlog.service'

interface CreateBlogPostProps {
  pagelistingId: string
  listingPageReturnUrl: string
}

const CreateBlogPost: FunctionComponent<CreateBlogPostProps> = ({ pagelistingId, listingPageReturnUrl }) => {
  const { t } = useTranslation()
  const { getQueryParam } = useQueryParams()

  const [preamble, setPreamble] = useState('')
  const [body, setBody] = useState('')

  const [returnUrl] = useState<string | null>(getQueryParam('returnurl') || null)
  const [blogPostId] = useState<string | null>(getQueryParam('blogPostId') || null)
  const [topImage, setTopImage] = useState<FileList | null>(null)
  const [attachment, setAttachment] = useState<FileList | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleTopImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setTopImage(e.target.files)
    }
  }

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachment(e.target.files)
    }
  }

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm<BlogItem>({
    defaultValues: {
      attachment: null,
      topImage: null,
    },
  })

  const onSubmit = (data: BlogItem) => {
    setIsLoading(true)

    data.blogListingPageId = parseInt(pagelistingId)
    data.attachment = attachment
    data.topImage = topImage

    if (blogPostId) {
      data.communityBlogPageId = parseInt(blogPostId)
      blogService.updateBlogPost(data).then(() => {
        setIsLoading(false)
        window.location.href = returnUrl ?? listingPageReturnUrl
      })
    } else {
      blogService.createBlogPost(data).then(() => {
        setIsLoading(false)
        window.location.href = returnUrl ?? listingPageReturnUrl
      })
    }
  }

  const onCancel = () => {
    window.location.href = returnUrl ?? listingPageReturnUrl
  }

  const getBlogPost = () => {
    setIsLoading(true)
    blogService
      .getBlogPost(blogPostId as string)
      .then((result) => {
        setValue('heading', result.heading)
        setValue('preamble', result.preamble)
        setValue('body', result.body)

        setPreamble(result.preamble)
        setBody(result.body)
      })
      .catch(() => {
        console.error('Det gick inte att hämta blogginlägg')
      })
    setIsLoading(false)
  }

  const isEditorContentEmpty = (value: string) => {
    const stripped = value
      ?.replace(/<p><br><\/p>/g, '')
      .replace(/<(.|\n)*?>/g, '')
      .trim()
    return stripped ? stripped?.length === 0 : true
  }

  useEffect(() => {
    register('preamble', {
      maxLength: {
        value: 300,
        message: t('blog.create-blog-post-preamble-length-validation'),
      },
      validate: (value) => !isEditorContentEmpty(value) || t('blog.create-blog-post-preamble-required-validation'),
    })

    register('body', {
      validate: (value) => !isEditorContentEmpty(value) || t('blog.create-blog-post-body-required-validation'),
    })
  }, [register])

  useEffect(() => {
    if (blogPostId) getBlogPost()
  }, [blogPostId])

  useEffect(() => {
    return () => {
      reset()
      setBody('')
      setPreamble('')
    }
  }, [])

  return (
    <Form className="position-relative p-3 bg-light" onSubmit={handleSubmit(onSubmit)}>
      {isLoading && (
        <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center z-1">
          <Spinner variant="primary" animation="border" />
        </div>
      )}
      <Form.Group>
        <Form.Label>{t('heading')}</Form.Label>
        <Form.Control
          type="text"
          {...register('heading', {
            required: t('blog.create-blog-post-heading-required-validation'),
            maxLength: {
              value: 75,
              message: t('blog.create-blog-post-heading-length-validation'),
            },
          })}
          placeholder={t('blog.create-blog-post-heading-placeholder')}
        />
        {errors.heading && <Form.Text className="text-danger">{errors.heading.message}</Form.Text>}
      </Form.Group>
      <Form.Group>
        <Form.Label>{t('image')}</Form.Label>
        <Form.Control
          type="file"
          {...register('topImage', {
            validate: {
              fileSize: (file: FileList | null) => {
                if (!file) return
                const maxSizeInMB = 2
                return file && file[0].size <= maxSizeInMB * 1024 * 1024
                  ? true
                  : `${t('workrooms.documents.new-file-form-file-size-validation')} ${maxSizeInMB} ${t('workrooms.documents.mb')}`
              },
            },
          })}
          isInvalid={!!errors.topImage}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTopImageChange(e)}
          className={`${errors?.topImage ? 'is-invalid' : ''}`}
        />
        <Form.Control.Feedback type="invalid">{errors.topImage?.message}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group>
        <Form.Label>{t('attachment')}</Form.Label>
        <Form.Control
          type="file"
          {...register('attachment', {
            validate: {
              fileSize: (file: FileList | null) => {
                if (!file) return
                const maxSizeInMB = 5
                return file && file[0].size <= maxSizeInMB * 1024 * 1024
                  ? true
                  : `${t('workrooms.documents.new-file-form-file-size-validation')} ${maxSizeInMB} ${t('workrooms.documents.mb')}`
              },
            },
          })}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAttachmentChange(e)}
          className={`${errors?.attachment ? 'is-invalid' : ''}`}
        />
        {errors?.attachment && <p className="invalid-feedback">{errors?.attachment.message}</p>}
      </Form.Group>
      <Form.Group>
        <Form.Label htmlFor="input-preamble">{t('blog.create-blog-post-preamble-title')}</Form.Label>
        <ReactQuill
          value={preamble}
          id="input-preamble"
          className="notice__quill-editor"
          theme="snow"
          modules={quillModules}
          placeholder={t('blog.create-blog-post-preamble-placeholder')}
          onChange={(value) => {
            setPreamble(value)
            setValue('preamble', value, { shouldValidate: true })
          }}
        />
        {errors?.preamble && <p className="text-danger">{errors?.preamble.message}</p>}
      </Form.Group>
      <Form.Group>
        <Form.Label htmlFor="input-body">{t('text')}</Form.Label>
        <ReactQuill
          value={body}
          id="input-body"
          className="notice__quill-editor"
          theme="snow"
          modules={quillModules}
          placeholder={t('blog.create-blog-post-body-placeholder')}
          onChange={(value) => {
            setBody(value)
            setValue('body', value, { shouldValidate: true })
          }}
        />
        {errors.body && <Form.Text className="text-danger">{errors.body.message}</Form.Text>}
      </Form.Group>
      <div className="d-flex gap-3 mt-3">
        <Button type="submit" disabled={isLoading}>
          {t('publish')}
        </Button>
        <Button type="reset" onClick={() => onCancel()} disabled={isLoading}>
          {t('abort')}
        </Button>
      </div>
    </Form>
  )
}

export default CreateBlogPost
